/**
 * Employees List Page
 * 
 * Displays all employees/caregivers with search and filters.
 * Integrated with Universal Intake Engine for AI-assisted data entry.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Search, Plus, Users, UserCheck, Clock, AlertCircle,
  ChevronRight, Phone, Calendar, Wand2, Filter, 
  Shield, GraduationCap, MapPin, Mail
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { UniversalIntakeForm } from "../../components/ui/universal-intake-form";
import { toast } from "sonner";
import api from "../../lib/api";

// Status badge styles
const STATUS_STYLES = {
  active: "bg-green-100 text-green-800 border-green-200",
  onboarding: "bg-blue-100 text-blue-800 border-blue-200",
  inactive: "bg-gray-100 text-gray-600 border-gray-200",
  terminated: "bg-red-100 text-red-600 border-red-200",
  on_leave: "bg-amber-100 text-amber-800 border-amber-200",
};

const STATUS_LABELS = {
  active: "Active",
  onboarding: "Onboarding",
  inactive: "Inactive",
  terminated: "Terminated",
  on_leave: "On Leave",
};

export default function EmployeesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAIIntake, setShowAIIntake] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onboarding: 0,
    expiringSoon: 0,
  });

  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (search) {
        params.append("search", search);
      }
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees?${params.toString()}`, {
        credentials: "include",
      });
      
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
        
        // Calculate stats
        const active = data.filter(e => e.status === "active").length;
        const onboarding = data.filter(e => e.status === "onboarding").length;
        const expiringSoon = data.filter(e => {
          if (!e.guardian_expiration) return false;
          const exp = new Date(e.guardian_expiration);
          const thirtyDays = new Date();
          thirtyDays.setDate(thirtyDays.getDate() + 30);
          return exp < thirtyDays && exp > new Date();
        }).length;
        
        setStats({
          total: data.length,
          active,
          onboarding,
          expiringSoon,
        });
      }
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Filter employees by search
  const filteredEmployees = employees.filter(emp => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      emp.first_name?.toLowerCase().includes(searchLower) ||
      emp.last_name?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      emp.phone?.includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Employees</h1>
          <p className="text-muted-foreground">
            Manage caregiver profiles, credentials, and assignments
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowAIIntake(true)}
            data-testid="ai-intake-employee-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            AI Quick Entry
          </Button>
          <Link to="/hiring">
            <Button data-testid="go-to-hiring-btn">
              <Plus className="w-4 h-4 mr-2" />
              New from Hiring
            </Button>
          </Link>
        </div>
      </div>

      {/* AI Intake Modal for Quick Employee Entry */}
      {showAIIntake && (
        <UniversalIntakeForm
          entityType="employee"
          isModal={true}
          onClose={() => setShowAIIntake(false)}
          onDataExtracted={async (extractedData) => {
            try {
              // Create employee with extracted data
              const response = await api.post('/employees', {
                first_name: extractedData.first_name,
                last_name: extractedData.last_name,
                email: extractedData.email,
                phone: extractedData.phone,
                date_of_birth: extractedData.date_of_birth,
                address: extractedData.address,
                city: extractedData.city,
                state: extractedData.state,
                zip_code: extractedData.zip_code,
                hire_date: extractedData.hire_date,
                job_title: extractedData.job_title || 'Home Health Aide',
                hourly_rate: extractedData.hourly_rate,
                employment_type: extractedData.employment_type || 'full_time',
                guardian_number: extractedData.guardian_number,
                guardian_expiration: extractedData.guardian_expiration,
                cpr_expiration: extractedData.cpr_expiration,
                tb_expiration: extractedData.tb_expiration,
                skills: extractedData.skills,
                languages: extractedData.languages,
                status: 'onboarding',
              });
              
              if (response?.data?.id) {
                toast.success('Employee created successfully!');
                setShowAIIntake(false);
                fetchEmployees();
                // Navigate to the new employee
                navigate(`/employees/${response.data.id}`);
              }
            } catch (err) {
              console.error('Failed to create employee:', err);
              toast.error(err.response?.data?.detail || 'Failed to create employee. Please try again.');
            }
          }}
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Onboarding</p>
                <p className="text-2xl font-bold">{stats.onboarding}</p>
              </div>
              <GraduationCap className="w-8 h-8 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-2xl font-bold">{stats.expiringSoon}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="employee-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employees</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="on_leave">On Leave</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Employees List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading employees...</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-semibold mb-2">No employees found</h3>
            <p className="text-muted-foreground mb-4">
              {search || statusFilter !== "all" 
                ? "Try adjusting your search or filters" 
                : "Start by adding your first employee"}
            </p>
            <Button onClick={() => setShowAIIntake(true)}>
              <Wand2 className="w-4 h-4 mr-2" />
              Add Employee with AI
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredEmployees.map((employee) => (
            <Link
              key={employee.id}
              to={`/employees/${employee.id}`}
              className="block"
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`employee-card-${employee.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <span className="text-lg font-semibold text-primary">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </span>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">
                            {employee.first_name} {employee.last_name}
                          </h3>
                          <Badge 
                            variant="outline"
                            className={STATUS_STYLES[employee.status] || STATUS_STYLES.active}
                          >
                            {STATUS_LABELS[employee.status] || employee.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {employee.job_title || 'Home Health Aide'}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          {employee.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {employee.phone}
                            </span>
                          )}
                          {employee.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {employee.email}
                            </span>
                          )}
                          {employee.hire_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Hired {formatDate(employee.hire_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Credential indicators */}
                      <div className="flex gap-2">
                        {employee.guardian_number && (
                          <Badge variant="outline" className="text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            Guardian
                          </Badge>
                        )}
                        {employee.languages?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {employee.languages.slice(0, 2).join(', ')}
                          </Badge>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
